// Payment schedules, cash and recognized revenue (PRD §3.5, PE7).

import { addMonths, daysBetween, daysInMonth, eachMonth, firstDayOf, lastDayOf, type Iso, type Month } from "./dates";

export type RevenueType = "client-fee" | "partner-funding" | "referral" | "reimbursable";

/** One invoice: when it is billed, and which service period it pays for. */
export interface PaymentLine {
  amount: number;
  invoiceMonth: Month;
  serviceStart: Iso;
  serviceEnd: Iso;
  type: RevenueType;
}

/** How people describe a schedule: "30k up front for 3 months, then 15k a month for 12". */
export interface ScheduleTier {
  amount: number;
  frequency: "once" | "monthly" | "quarterly";
  count: number;
  firstInvoiceMonth: Month;
  /** Months of service each payment covers: 1 for a monthly fee, 3 for a quarter in advance. */
  monthsCovered: number;
  type?: RevenueType;
}

// --- Entry (PE7, rev. 1.7) ---------------------------------------------------------------------------
// A line is entered the way it is stated: an amount per period of How often, for a number of
// periods ("$19,167 a month for 8 months"). A one-time payment states the months of service it pays
// for instead. Storage keeps `count` and `monthsCovered`, so revenue rules and baselines don't move.

/** Months between invoices. */
export const periodMonths = (frequency: ScheduleTier["frequency"]): number => (frequency === "quarterly" ? 3 : 1);

/** What the entry's "Covers" field shows: periods for a repeating line, months of service for a one-time one. */
export const coverCount = (t: Pick<ScheduleTier, "frequency" | "count" | "monthsCovered">): number => (t.frequency === "once" ? t.monthsCovered : t.count);

/** Sets the "Covers" field: N periods, each payment paying for its own period; or N months for a one-time payment. */
export function withCover<T extends ScheduleTier>(t: T, n: number): T {
  return t.frequency === "once" ? { ...t, count: 1, monthsCovered: n } : { ...t, count: n, monthsCovered: periodMonths(t.frequency) };
}

/** Months of service a line pays for, from its first invoice to the end of the last payment's period. */
export const serviceMonths = (t: Pick<ScheduleTier, "frequency" | "count" | "monthsCovered">): number =>
  t.frequency === "once" ? t.monthsCovered : (t.count - 1) * periodMonths(t.frequency) + t.monthsCovered;

/** Changes How often while keeping the months of service the line pays for. */
export function withFrequency<T extends ScheduleTier>(t: T, frequency: ScheduleTier["frequency"]): T {
  const months = serviceMonths(t);
  return withCover({ ...t, frequency }, frequency === "once" ? months : Math.max(1, Math.round(months / periodMonths(frequency))));
}

/** A repeating line whose payments don't each pay for exactly their own period: entered before rev. 1.7. */
export const isIrregular = (t: Pick<ScheduleTier, "frequency" | "monthsCovered">): boolean => t.frequency !== "once" && t.monthsCovered !== periodMonths(t.frequency);

/**
 * Reads an irregular line the way rev. 1.7 enters it: the same amount per period, across the same
 * months of service. "Monthly, 1 time, each covering 8 months" becomes the same amount a month for 8.
 */
export const regularized = <T extends ScheduleTier>(t: T): T => (isIrregular(t) ? withFrequency(t, t.frequency) : t);

export function expandTiers(tiers: readonly ScheduleTier[]): PaymentLine[] {
  const lines: PaymentLine[] = [];
  for (const tier of tiers) {
    const step = tier.frequency === "quarterly" ? 3 : 1;
    const count = tier.frequency === "once" ? 1 : tier.count;
    for (let i = 0; i < count; i++) {
      const invoiceMonth = addMonths(tier.firstInvoiceMonth, i * step);
      lines.push({
        amount: tier.amount,
        invoiceMonth,
        serviceStart: firstDayOf(invoiceMonth),
        serviceEnd: lastDayOf(addMonths(invoiceMonth, tier.monthsCovered - 1)),
        type: tier.type ?? "client-fee",
      });
    }
  }
  return lines;
}

export const contractValue = (lines: readonly PaymentLine[]): number => lines.reduce((sum, l) => sum + l.amount, 0);

/** Cash: each amount lands in its invoice month. */
export function cashByMonth(lines: readonly PaymentLine[]): Map<Month, number> {
  const out = new Map<Month, number>();
  for (const l of lines) out.set(l.invoiceMonth, (out.get(l.invoiceMonth) ?? 0) + l.amount);
  return out;
}

/**
 * Recognized: each amount spread evenly over the calendar months of its service period, a partial
 * month weighted by the share of its days covered. A payment covering three whole months
 * recognizes one third in each.
 */
export function recognizedByMonth(lines: readonly PaymentLine[]): Map<Month, number> {
  const out = new Map<Month, number>();
  for (const l of lines) {
    const months = [...eachMonth(l.serviceStart, l.serviceEnd)];
    const weights = months.map((m) => {
      const from = l.serviceStart > firstDayOf(m) ? l.serviceStart : firstDayOf(m);
      const to = l.serviceEnd < lastDayOf(m) ? l.serviceEnd : lastDayOf(m);
      return (daysBetween(from, to) + 1) / daysInMonth(m);
    });
    const total = weights.reduce((a, b) => a + b, 0);
    months.forEach((m, i) => out.set(m, (out.get(m) ?? 0) + (l.amount * weights[i]) / total));
  }
  return out;
}
